import React from 'react';
import { useRealtorContext } from '../context/RealtorContext';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import Section from '../components/layout/Section';
import Button from '../components/ui/Button';
import Card from '../components/ui/Card';
import FormInput from '../components/ui/FormInput';
import FormSelect from '../components/ui/FormSelect';
import FileUpload from '../components/ui/FileUpload';

const RealtorOnboardingPage: React.FC = () => {
  const { addRealtor, loading, error } = useRealtorContext();
  
  // Form state
  const [formData, setFormData] = React.useState({
    name: '',
    email: '',
    phone: '',
    role: '',
    password: '',
    confirmPassword: '',
    profileImage: null as File | null,
    agreeToTerms: false
  });
  
  // State for image preview URL
  const [imagePreviewUrl, setImagePreviewUrl] = React.useState<string | null>(null);
  
  // Form errors
  const [errors, setErrors] = React.useState<Record<string, string>>({});
  
  // Success message
  const [showSuccess, setShowSuccess] = React.useState(false);
  
  // Handle input change
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    
    if (type === 'checkbox') {
      const checkbox = e.target as HTMLInputElement;
      setFormData(prev => ({
        ...prev,
        [name]: checkbox.checked
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
    
    // Clear error when user types
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };
  
  // Handle file upload
  const handleFileChange = (file: File | null) => {
    setFormData(prev => ({
      ...prev,
      profileImage: file
    }));

    if (file) {
      setImagePreviewUrl(URL.createObjectURL(file));
    } else {
      setImagePreviewUrl(null);
    }
    
    // Clear error
    if (errors.profileImage) {
      setErrors(prev => ({
        ...prev,
        profileImage: ''
      }));
    }
  };
  
  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate form
    const newErrors: Record<string, string> = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }
    
    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    }
    
    if (!formData.role) {
      newErrors.role = 'Role is required';
    }
    
    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    }
    
    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }
    
    if (!formData.agreeToTerms) {
      newErrors.agreeToTerms = 'You must agree to the terms and conditions';
    }
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    
    // Form is valid, create realtor
    try {
      const realtorData = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        role: formData.role,
        password: formData.password,
        joinDate: new Date().toISOString().split('T')[0],
        properties: 0,
        commission: 0,
        affiliates: [],
        commissionHistory: []
      };

      await addRealtor(realtorData, formData.profileImage);
      
      // Show success message
      setShowSuccess(true);
      
      // Reset form after 3 seconds
      setTimeout(() => {
        setShowSuccess(false);
        setFormData({
          name: '',
          email: '',
          phone: '',
          role: '',
          password: '',
          confirmPassword: '',
          profileImage: null,
          agreeToTerms: false
        });
        setImagePreviewUrl(null); // Clear image preview
      }, 3000);
    } catch (err) {
      console.error('Failed to create realtor:', err);
    }
  };
  
  // Role options
  const roleOptions = [
    { value: 'Junior Realtor', label: 'Junior Realtor' },
    { value: 'Realtor', label: 'Realtor' },
    { value: 'Senior Realtor', label: 'Senior Realtor' },
    { value: 'Team Lead', label: 'Team Lead' }
  ];

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-neutral-50 via-white to-primary-50/30">
      <Navbar />
      
      <main className="flex-grow pt-20">
        {/* Hero Section */}
        <div className="relative bg-gradient-to-br from-primary-900 via-primary-800 to-primary-700 text-white py-24 overflow-hidden">
          {/* Background decorations */}
          <div className="absolute inset-0">
            <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-secondary-500/10 rounded-full blur-3xl animate-float" />
            <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-primary-400/10 rounded-full blur-3xl animate-float" style={{ animationDelay: '1s' }} />
          </div>
          
          <div className="container mx-auto px-4 text-center relative z-10">
            <div className="max-w-4xl mx-auto">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full border border-white/20 mb-6">
                <span className="w-2 h-2 bg-accent-400 rounded-full animate-pulse-soft" />
                <span className="text-sm font-medium">Join Our Elite Team</span>
              </div>
              
              <h1 className="text-5xl md:text-6xl lg:text-7xl font-display font-bold mb-6 leading-tight">
                <span className="bg-gradient-to-r from-white via-white to-secondary-200 bg-clip-text text-transparent">
                  Become a Solar Estate
                </span>
                <br />
                <span className="bg-gradient-to-r from-secondary-300 to-secondary-500 bg-clip-text text-transparent">
                  Realtor Today
                </span>
              </h1>
              
              <p className="text-xl md:text-2xl mb-8 text-white/90 max-w-3xl mx-auto leading-relaxed">
                Join Unique Solar Gardens Estate's elite team of realtors and earn competitive commissions while helping clients find their dream sustainable homes.
              </p>

              {/* Stats */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-3xl mx-auto">
                <div className="p-6 rounded-2xl bg-white/10 backdrop-blur-sm border border-white/20">
                  <div className="text-3xl font-bold text-secondary-300 mb-2">5%</div>
                  <div className="text-sm text-white/80">Commission Rate</div>
                </div>
                <div className="p-6 rounded-2xl bg-white/10 backdrop-blur-sm border border-white/20">
                  <div className="text-3xl font-bold text-secondary-300 mb-2">2-Tier</div>
                  <div className="text-sm text-white/80">Affiliate System</div>
                </div>
                <div className="p-6 rounded-2xl bg-white/10 backdrop-blur-sm border border-white/20">
                  <div className="text-3xl font-bold text-secondary-300 mb-2">24/7</div>
                  <div className="text-sm text-white/80">Support Available</div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Registration Form Section */}
        <Section className="py-20">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-display font-bold text-primary-800 mb-4">
                Start Your Journey
              </h2>
              <p className="text-xl text-neutral-600 max-w-2xl mx-auto">
                Fill out the form below to join our team and start earning commissions from sustainable real estate sales.
              </p>
            </div>

            <Card className="p-8 lg:p-12" shadow="large" glass>
              {error && (
                <div className="mb-8 p-4 bg-red-50 border border-red-200 text-red-800 rounded-2xl flex items-center gap-3 animate-fade-in-up">
                  <svg className="w-5 h-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <p className="font-medium">{error}</p>
                </div>
              )}
              
              {showSuccess && (
                <div className="mb-8 p-6 bg-gradient-to-r from-accent-50 to-accent-100 border border-accent-200 text-accent-800 rounded-2xl animate-fade-in-up">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-8 h-8 bg-accent-500 rounded-full flex items-center justify-center">
                      <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <h3 className="font-semibold text-lg">Registration Successful!</h3>
                  </div>
                  <p className="text-accent-700">Welcome to the team! You will be contacted by our team shortly with next steps.</p>
                </div>
              )}
              
              <form onSubmit={handleSubmit} className="space-y-8">
                {/* Personal Information */}
                <div>
                  <h3 className="text-xl font-semibold text-primary-800 mb-6 flex items-center gap-2">
                    <div className="w-8 h-8 bg-gradient-to-r from-primary-500 to-primary-600 rounded-lg flex items-center justify-center text-white text-sm font-bold">1</div>
                    Personal Information
                  </h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <FormInput 
                      label="Full Name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="Enter your full name"
                      required
                      error={errors.name}
                    />
                    
                    <FormInput 
                      label="Email Address"
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="Enter your email address"
                      required
                      error={errors.email}
                    />
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                    <FormInput 
                      label="Phone Number"
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="Enter your phone number"
                      required
                      error={errors.phone}
                    />
                    
                    <FormSelect 
                      label="Realtor Role"
                      name="role"
                      value={formData.role}
                      onChange={handleChange}
                      options={roleOptions}
                      placeholder="Select your desired role"
                      required
                      error={errors.role}
                    />
                  </div>
                </div>

                {/* Security */}
                <div>
                  <h3 className="text-xl font-semibold text-primary-800 mb-6 flex items-center gap-2">
                    <div className="w-8 h-8 bg-gradient-to-r from-primary-500 to-primary-600 rounded-lg flex items-center justify-center text-white text-sm font-bold">2</div>
                    Account Security
                  </h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <FormInput 
                      label="Password"
                      type="password"
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      placeholder="Create a strong password"
                      required
                      error={errors.password}
                    />
                    
                    <FormInput 
                      label="Confirm Password"
                      type="password"
                      name="confirmPassword"
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      placeholder="Confirm your password"
                      required
                      error={errors.confirmPassword}
                    />
                  </div>
                </div>

                {/* Profile Picture */}
                <div>
                  <h3 className="text-xl font-semibold text-primary-800 mb-6 flex items-center gap-2">
                    <div className="w-8 h-8 bg-gradient-to-r from-primary-500 to-primary-600 rounded-lg flex items-center justify-center text-white text-sm font-bold">3</div>
                    Profile Picture
                  </h3>
                  
                  <FileUpload
                    label="Upload Profile Picture"
                    name="profileImage"
                    onFileSelect={handleFileChange}
                    accept="image/*"
                    error={errors.profileImage}
                    helperText="Upload a professional photo of yourself (optional but recommended)"
                    selectedFileProp={formData.profileImage} // Pass the selected file for display
                  />
                </div>

                {/* Terms and Conditions */}
                <div>
                  <h3 className="text-xl font-semibold text-primary-800 mb-6 flex items-center gap-2">
                    <div className="w-8 h-8 bg-gradient-to-r from-primary-500 to-primary-600 rounded-lg flex items-center justify-center text-white text-sm font-bold">4</div>
                    Terms & Conditions
                  </h3>
                  
                  <div className="p-6 bg-gradient-to-r from-neutral-50 to-primary-50/30 rounded-2xl border border-neutral-200/50">
                    <div className="flex items-start gap-4">
                      <div className="flex items-center h-6 mt-1">
                        <input
                          id="agreeToTerms"
                          name="agreeToTerms"
                          type="checkbox"
                          checked={formData.agreeToTerms}
                          onChange={handleChange}
                          className="w-5 h-5 text-primary-600 border-neutral-300 rounded-lg focus:ring-primary-500 focus:ring-2"
                        />
                      </div>
                      <div className="text-sm">
                        <label htmlFor="agreeToTerms" className="font-medium text-neutral-700 cursor-pointer">
                          I agree to the{' '}
                          <a href="#" className="text-primary-600 hover:text-primary-700 underline decoration-primary-300 hover:decoration-primary-500 transition-colors">
                            Terms and Conditions
                          </a>
                          {' '}and{' '}
                          <a href="#" className="text-primary-600 hover:text-primary-700 underline decoration-primary-300 hover:decoration-primary-500 transition-colors">
                            Privacy Policy
                          </a>
                        </label>
                        {errors.agreeToTerms && (
                          <p className="mt-2 text-red-500 text-sm">{errors.agreeToTerms}</p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Submit Button */}
                <div className="pt-6">
                  <Button 
                    type="submit"
                    variant="gradient"
                    size="xl"
                    fullWidth
                    loading={loading}
                    icon={
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                      </svg>
                    }
                    iconPosition="right"
                  >
                    {loading ? 'Creating Your Account...' : 'Join Our Team'}
                  </Button>
                </div>
              </form>
            </Card>
          </div>
        </Section>
        
        {/* Benefits Section */}
        <Section className="py-20 bg-gradient-to-br from-primary-50 via-white to-secondary-50/30">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-display font-bold text-primary-800 mb-4">
              Why Join Our Team?
            </h2>
            <p className="text-xl text-neutral-600 max-w-3xl mx-auto">
              Discover the benefits of being part of the leading solar-powered real estate team.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <Card hover className="p-8 text-center" shadow="medium">
              <div className="w-16 h-16 bg-gradient-to-r from-secondary-500 to-secondary-600 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-glow-secondary">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-primary-800 mb-4">Competitive Commissions</h3>
              <p className="text-neutral-600 leading-relaxed mb-6">
                Earn up to 5% commission on every property sale, with additional performance bonuses for top performers.
              </p>
              <div className="space-y-2">
                <div className="flex items-center justify-between p-3 bg-secondary-50 rounded-xl">
                  <span className="text-sm font-medium text-neutral-700">Base Commission</span>
                  <span className="text-lg font-bold text-secondary-600">5%</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-secondary-50 rounded-xl">
                  <span className="text-sm font-medium text-neutral-700">Performance Bonus</span>
                  <span className="text-lg font-bold text-secondary-600">+2%</span>
                </div>
              </div>
            </Card>
            
            <Card hover className="p-8 text-center" shadow="medium">
              <div className="w-16 h-16 bg-gradient-to-r from-primary-500 to-primary-600 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-glow">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-primary-800 mb-4">2-Tier Affiliate Program</h3>
              <p className="text-neutral-600 leading-relaxed mb-6">
                Build your network and earn additional income through our comprehensive affiliate system.
              </p>
              <div className="space-y-2">
                <div className="flex items-center justify-between p-3 bg-primary-50 rounded-xl">
                  <span className="text-sm font-medium text-neutral-700">Level 1 Referrals</span>
                  <span className="text-lg font-bold text-primary-600">3%</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-primary-50 rounded-xl">
                  <span className="text-sm font-medium text-neutral-700">Level 2 Referrals</span>
                  <span className="text-lg font-bold text-primary-600">1%</span>
                </div>
              </div>
            </Card>
            
            <Card hover className="p-8 text-center" shadow="medium">
              <div className="w-16 h-16 bg-gradient-to-r from-accent-500 to-accent-600 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-primary-800 mb-4">Career Growth</h3>
              <p className="text-neutral-600 leading-relaxed mb-6">
                Benefit from professional development opportunities and a clear advancement path.
              </p>
              <div className="space-y-2">
                <div className="p-3 bg-accent-50 rounded-xl">
                  <span className="text-sm font-medium text-accent-700">Training Programs</span>
                </div>
                <div className="p-3 bg-accent-50 rounded-xl">
                  <span className="text-sm font-medium text-accent-700">Mentorship</span>
                </div>
                <div className="p-3 bg-accent-50 rounded-xl">
                  <span className="text-sm font-medium text-accent-700">Leadership Opportunities</span>
                </div>
              </div>
            </Card>
          </div>
        </Section>
      </main>
      
      <Footer />
    </div>
  );
};

export default RealtorOnboardingPage;